'use client';
import { RadioChangeEvent } from 'antd/lib/radio';
import { Upload, Button, Radio } from 'antd';

import { TrackingManager } from '@benzinga/tracking-manager';
import styled, { css } from '@benzinga/themetron';

import Papa from 'papaparse';
import React from 'react';

import { SessionContext } from '@benzinga/session-context';
import { WatchlistId, WatchlistManager } from '@benzinga/watchlist-manager';
import { Modal } from '@benzinga/core-ui';
import { useWatchlist } from '@benzinga/watchlist-manager-hooks';
import { capitalize, isNil } from '@benzinga/utils';
import { LoggingManager } from '@benzinga/session';
import { UpArrow } from '@benzinga/themed-icons';
import { RcFile, UploadFile } from 'antd/es/upload/interface';

interface Props {
  closeModal: () => void;
  watchlistId?: WatchlistId;
}

interface State {
  file: RcFile | null;
  fileUploaded: boolean;
  importAction: ImportAction;
  isImporting: boolean;
}

enum ImportAction {
  replace = 'replace',
  update = 'update',
}

enum SupportedFileFormat {
  CSV = 'text/csv',
  EXCEL_CSV = 'application/vnd.ms-excel',
}

const updateText = `${capitalize(ImportAction.update)}: Add new symbols to your existing Watchlist`;
const replaceText = `${capitalize(ImportAction.replace)}: Replace your watchlist with new list`;

export const WatchlistImport: React.FC<Props> = props => {
  const session = React.useContext(SessionContext);
  const watchlist = useWatchlist(props.watchlistId ?? '');
  const logger = session.getManager(LoggingManager);
  const logMessage = React.useCallback(
    (level: 'log' | 'error' | 'info', category: string, message: string) =>
      logger.log(level, { category, message }, ['toast']),
    [logger],
  );
  const { closeModal, watchlistId } = props;

  const [state, setState] = React.useState<State>({
    file: null,
    fileUploaded: false,
    importAction: ImportAction.update,
    isImporting: false,
  });

  const { file, fileUploaded, importAction, isImporting } = state;

  React.useEffect(() => {
    session.getManager(TrackingManager).trackWatchlistEvent('import', {
      watchlist_id: watchlist?.watchlistId,
    });
  }, [session, watchlist?.watchlistId]);

  const parseFile = (file: RcFile): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        complete: results => {
          const { data } = results;
          const [header, ...rows] = data as string[][];
          const symbolHeader = header.find(h => h.toLowerCase().includes('symbol'));
          const symbolIndex = symbolHeader ? header.indexOf(symbolHeader) : 0;

          const parsedSymbols = rows
            .map(row => row[symbolIndex]?.replace(/\$/g, '').trim())
            .filter((symbol): symbol is string => !!symbol && symbol.toLowerCase() !== 'symbol');

          const uniqueSymbols = Array.from(new Set(parsedSymbols));

          resolve(uniqueSymbols);
        },
        error: (error: Error) => {
          reject(error);
        },
        skipEmptyLines: true,
      });
    });
  };

  const doSync = React.useCallback(async () => {
    const { file, importAction } = state;

    if (isNil(file) || watchlist === undefined) {
      logMessage('error', 'watchlist', 'No file selected or watchlist not available');
      return;
    }

    setState(old => ({ ...old, isImporting: true }));

    try {
      const parsedSymbols = await parseFile(file);

      if (parsedSymbols.length === 0) {
        logMessage('info', 'watchlist file import', 'No valid symbols found in the imported file.');
        setState(old => ({ ...old, isImporting: false }));
        return;
      }

      const watchlistManager = session.getManager(WatchlistManager);
      const existingSymbols: string[] = watchlist.symbols?.map(s => s.symbol) ?? [];

      if (importAction === ImportAction.replace) {
        const symbolsToRemove = watchlist.symbols.filter(s => !parsedSymbols.includes(s.symbol));
        if (symbolsToRemove.length > 0) {
          await watchlistManager.removeTickersFromWatchlist(watchlist, symbolsToRemove);
        }
      }

      const symbolsToAdd = parsedSymbols.filter(s => !existingSymbols.includes(s));
      if (symbolsToAdd.length > 0) {
        await watchlistManager.addTickersToWatchlist(watchlist, symbolsToAdd);
      } else if (importAction === ImportAction.update) {
        logMessage('info', 'watchlist file import', 'All tickers in file are already in watchlist');
      }

      logMessage('log', 'watchlist file import', 'Watchlist imported successfully.');

      closeModal();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      logMessage('error', 'watchlist file import', `Failed to parse CSV file: ${errorMessage}`);
    } finally {
      setState(old => ({ ...old, file: null, isImporting: false }));
    }
  }, [state, watchlist, logMessage, session, closeModal]);

  const trackEvent = (value: string) => {
    const capitalizeValue = capitalize(value);
    session.getManager(TrackingManager).trackWatchlistEvent('import', {
      watchlist_id: capitalizeValue,
    });
  };

  const handleImportActionChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    trackEvent(value);
    setState(old => ({ ...old, importAction: value }));
  };

  const handleBeforeFileUpload = (file: RcFile): boolean => {
    const isCsv = file.type === SupportedFileFormat.CSV || file.type === SupportedFileFormat.EXCEL_CSV;
    if (isCsv) {
      setState(old => ({ ...old, file, fileUploaded: true }));
    } else {
      logMessage('error', 'watchlist', 'Invalid format. Only CSV supported atm.');
    }
    return isCsv;
  };

  const handleOnRemoveUploadedFile = (_file: UploadFile): boolean => {
    setState(old => ({
      ...old,
      file: null,
      fileUploaded: false,
    }));

    return true;
  };

  const uploaderProps = {
    beforeUpload: handleBeforeFileUpload,
    fileList: file ? [file] : [],
    onRemove: handleOnRemoveUploadedFile,
    showUploadList: fileUploaded,
  };

  if (watchlistId !== null && watchlist?.name !== null) {
    return (
      <Modal
        onClose={closeModal}
        size={{ alert: true }}
        title={
          <ModalTitleSpan>
            Import Symbols to&nbsp;{' '}
            <span className="u-primaryText pre" title={watchlist?.name}>
              {watchlist?.name}
            </span>
          </ModalTitleSpan>
        }
      >
        <div className="Modal-content">
          <div className="Modal-title">Add Symbols</div>
          <div className="Modal-subtitle">
            <a
              href="https://help.benzinga.com/en/articles/1413152-how-do-i-import-a-watchlist"
              rel="noreferrer"
              target="_blank"
            >
              Visit our help page on watchlist syncing and supported file formats
            </a>
          </div>

          <Radio.Group onChange={handleImportActionChange} value={importAction}>
            <div className="Modal-option">
              <StyledRadio value={ImportAction.update}>
                <span className="Modal-option-text">{updateText}</span>
              </StyledRadio>
            </div>

            <div className="Modal-option">
              <StyledRadio value={ImportAction.replace}>
                <span className="Modal-option-text">{replaceText}</span>
              </StyledRadio>
            </div>
          </Radio.Group>

          <UploadDiv className="Modal-proceed" hideSelect={fileUploaded}>
            <Upload {...uploaderProps}>
              <UploadFileButton>
                Upload <UpArrow title="Upload File" />
              </UploadFileButton>
            </Upload>
          </UploadDiv>

          <div className="Modal-proceed">
            <button className="Button Button--text" onClick={closeModal}>
              Cancel
            </button>
            <button autoFocus className="Button Button--primary" disabled={isImporting} onClick={doSync}>
              {isImporting ? 'Importing...' : 'Import/Sync'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return null;
};

const ModalTitleSpan = styled.span`
  max-width: 100%;
  vertical-align: bottom;
  .u-primaryText {
    display: inline-block;
    max-width: 70%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: inherit;
  }
`;

const StyledRadio = styled(Radio)`
  display: flex !important;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
`;

const UploadFileButton = styled(Button)``;

const UploadDiv = styled.div<{ hideSelect?: boolean }>`
  text-align: left !important;

  .ant-upload-list-item {
    margin-top: 0 !important;
    padding: 15px 10px;
    border: 1px solid ${props => props.theme.colors.border};
    background-color: ${props => props.theme.colors.background};

    .ant-upload-list-item-name {
      font-weight: 500;
    }
  }

  .ant-upload-list {
    padding: 0 !important;
  }

  ${props =>
    props.hideSelect &&
    css`
      .ant-upload-select {
        display: none !important;
      }
    `}
`;
